/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { concat, isEmpty, orderBy, uniq } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import noImage from '../../../../assets/images/no-image.png';
import { shortenandHoverString, checkArray } from '../../../../Components/helper';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { useProductInformationState } from '../Provider';
import { getProductCompletion, unlinkDevOrder, getReorderList } from '../../store/DevelopmentOrder.action';
import ReorderTitleComponent from './ReorderTitleComponent';
import { formatClusterOptionLabel, buildParentClusterOption } from './clusterOptionLabel';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import { BombiixCard } from '../../../../Components/Card/BombiixCard';
import {
  ProgressIndicatorIcon,
  EditIcon,
  ArchiveIcon,
  DuplicateIcon,
  ProductGroupIcon,
  ProductInfoIcon,
  DeleteIcon,
  UnlinkIcon,
  HistoryIcon,
} from '../../../../bombiix-icon';

import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import { useProductEditNavigation } from '../../../../hooks/useProductEditNavigation';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';

function ProductCardList({ item, index, getReorderData, isEditIconVisible }) {
  const reducerData = useSelector((redState) => redState);
  const dispatch = useDispatch();
  const { goToProductEdit, goToProductDuplicate } = useProductEditNavigation();
  const { t } = useTranslation('common');
  const lengthLabel = useLengthLabel(t('FILTER.LENGTH'));
  const { UserDetails, Users, SystemMastersDetails } = reducerData;
  const { isSupplierLoggedIn } = UserDetails;
  const { subBrandAccess } = SystemMastersDetails;
  const { logInRights, sectionAccess } = Users;
  const { CheckActionPermission } = useComponent();
  const ProductAddEditAccess = CheckActionPermission(logInRights, 'products', 'addProduct', 'addEdit');
  const ProductDeleteAccess = CheckActionPermission(logInRights, 'products', 'addProduct', 'delete');
  const ProductGroupToggle = sectionAccess?.masters?.productGroupsAccess;
  const [lengthEnabled, setLengthEnabled] = useState(true);
  const ProductGroupAddEditAccess = CheckActionPermission(logInRights, 'masters', 'productGroups', 'addEdit');

  const { state, setState, callGetDevorderListApi, cardData, setCardData, userRole } = useProductInformationState();
  const { archieveTechPackState, deleteTechPackState, trashTechPackState } = state;
  const checkStatus = state?.selectedIds?.includes(item?.clusterId ? item.clusterId : item._id);
  const [cardState, setCardState] = useState({
    isUnlinkOpen: false,
  });
  const { expandCard, selectedClusterIds } = cardData;
  const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserInfo'));
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('loggedInUserInfo'));
    if (userData?.lengthVisible !== null) {
      setLengthEnabled(userData?.lengthVisible);
    }
  }, []);
  const subdomain = window.location.hostname.split('.')[0];

  const supplierCompany = loggedInUserInfo?.userCompanies?.companiesList?.find((company) => company?.companyName === subdomain);

  const onBtnClick = (value) => {
    setCardData((prevData) => ({
      ...prevData,
      selectedClusterIds: { ...prevData?.selectedClusterIds, [item?._id]: value?._id },
    }));
  };

  const handleUnlink = () => {
    setCardState((prevState) => ({
      ...prevState,
      isUnlinkOpen: true,
    }));
  };

  const showDynamicValue = (ele) => {
    if (ele.type === 'dropdown' || ele.type === 'radio-button') {
      return ele?.selectedOpt ? ele?.selectedOpt?.keyName : '-';
    }
    return ele?.valueText ? ele?.valueText : '-';
  };

  /**
   * Resolves the 50px thumbnail source. Uses `thumbNailImg` — the low-resolution
   * cropped image — so list rows stay light, falling back to the high-resolution
   * `image` when a record predates the thumbnail field.
   * @param {object} element the resolved card image element
   * @returns {string} presigned thumbnail URL, or the noImage placeholder
   */
  const imgSrc = (element) => {
    const thumbnail = element?.thumbNailImg || element?.image;
    return thumbnail && !isEmpty(thumbnail) && thumbnail !== null ? thumbnail : noImage;
  };

  /**
   * Resolves the source backing the enlarged hover preview. Uses `image` — the cropped
   * selection at its original resolution — so the popup shows the same framing as the
   * thumbnail without its downscaling. Falls back to the thumbnail when absent.
   * @param {object} element the resolved card image element
   * @returns {string} presigned URL for the hover preview
   */
  const previewImgSrc = (element) => {
    return element?.image && !isEmpty(element?.image) ? element?.image : imgSrc(element);
  };

  /**
   * Resolves the card image element, mirroring the source selection used for the thumb.
   * @param {object} selectedOrder the order currently picked in the card's dropdown
   * @returns {object} the element whose `image` backs the thumb and hover preview
   */
  const imgElement = (selectedOrder) => (window.location?.hostname?.includes('rekucci') ? item : selectedOrder) || item;

  /**
   * Whether the card has a real uploaded image, as opposed to the `noImage` placeholder.
   * The hover preview is suppressed for placeholders so an enlarged "No Image" never shows.
   * @param {object} selectedOrder the order currently picked in the card's dropdown
   * @returns {boolean} true when the resolved element carries an actual image
   */
  const hasRealImage = (selectedOrder) => {
    const element = imgElement(selectedOrder);
    const thumbnail = element?.thumbNailImg || element?.image;
    return Boolean(thumbnail) && !isEmpty(thumbnail) && thumbnail !== null;
  };

  const expandCollapse = (ind) => {
    setCardData((prevData) => ({
      ...prevData,
      expandCard: expandCard !== ind ? ind : '',
    }));
  };

  const handleEdit = (devId) => {
    goToProductEdit(devId);
  };

  const handleCheckbox = (e, devId) => {
    if (e?.target?.checked) {
      setState((prevData) => ({
        ...prevData,
        selectedIds: uniq(concat(state?.selectedIds, devId)),
      }));
    } else {
      const copyState = state?.selectedIds;
      const index = copyState?.indexOf(devId);
      if (index > -1) {
        copyState.splice(index, 1);
      }
      setState((prevData) => ({
        ...prevData,
        selectedIds: copyState,
      }));
    }
  };

  const handleCompletionIndicator = async (devOrder) => {
    const res = await dispatch(getProductCompletion(devOrder?._id, { percentage: true }));
    if (res?.code === 200) {
      setState((prevData) => ({
        ...prevData,
        isCompletionModalOpen: true,
        currentProduct: devOrder,
        completionData: res.data,
      }));
    }
  };

  const handleProductGroup = (devOrder) => {
    setState((prevData) => ({
      ...prevData,
      isProductGroupModalOpen: true,
      currentProduct: devOrder,
    }));
  };

  const handleArchiveProduct = (item) => {
    setState((prevData) => ({
      ...prevData,
      selectedIds: [item],
      isArchivedModal: !state?.isArchivedModal,
    }));
  };

  const handleTrashProduct = (item) => {
    setState((prevData) => ({
      ...prevData,
      selectedIds: [item],
      isTrashModal: !state?.isTrashModal,
    }));
  };

  const handleDelete = (id) => {
    setState((prevData) => ({
      ...prevData,
      isDeleteOpen: true,
      deletedDevOrderId: id,
    }));
  };

  const handleDuplicateProduct = (devId) => {
    goToProductDuplicate(devId);
  };

  const handleUnlinkProduct = async (id) => {
    const res = await dispatch(unlinkDevOrder(id, t));
    if (res === 200) {
      if (state?.isLinkOpen) {
        const obj = { archived: state?.activeList !== 'open' || false, devOrderId: item._id, deleted: state?.activeList === 'bin' || false };
        await dispatch(getReorderList(obj));
      } else {
        getDevOrderList(0);
      }
    }
  };

  const closeUnlinkModal = () => {
    setCardState((prevState) => ({
      ...prevState,
      isUnlinkOpen: false,
    }));
  };

  const handleUnlinkConfirmed = (devId) => {
    closeUnlinkModal();
    handleUnlinkProduct(devId);
  };

  const getDevOrderList = async (index) => {
    setState((prevData) => ({
      ...prevData,
      pageIndex: index,
      pageLoading: true,
      productData: [],
    }));
    const payloadObj = {
      archived: state?.activeList === 'archived' || state?.activeList === 'bin',
      count: index,
      searchText: state?.searchText,
      ...state?.filterData,
      ...state?.filterSort,
    };
    if (state?.selectedGroupId) {
      payloadObj.groupId = state?.selectedGroupId;
    }
    await callGetDevorderListApi(payloadObj);
  };

  const reOrderList = useMemo(() => {
    return checkArray(item?.reOrders) ? item?.reOrders?.map((ele) => ({
      _id: ele?._id,
      number: formatClusterOptionLabel(ele?.number, ele?.seasonId?.name, ele?.month, ele?.year),
      createdAt: ele?.createdAt,
      image: ele?.image,
      fullImg: ele?.fullImg,
      thumbNailImg: ele?.thumbNailImg,
    })) : [];
  }, [item?.reOrders]);

  const hasReOrders = reOrderList && reOrderList?.length > 0;

  const reOrderOption = useMemo(() => {
    if (checkArray(reOrderList)) {
      const parentOption = buildParentClusterOption(item);
      const orderBySlug = orderBy(
        [parentOption, ...reOrderList],
        ['createdAt'],
        ['desc'],
      );

      if (window.location?.hostname?.includes('rekucci')) {
        return orderBySlug?.reverse();
      }

      if (window.location?.hostname?.includes('radley')) {
        return [parentOption, ...reOrderList];
      }

      return orderBySlug;
    }

    return [];
  }, [reOrderList, item]);

  /*
   * The selection lives in the shared card state, not in this component: the masonry re-balances
   * its columns on every list update and remounts each card, which would otherwise drop the picked
   * order and re-run the load below against whichever order sorted first that render.
   */
  const activeDropdown = reOrderOption?.find((option) => option?._id === selectedClusterIds?.[item?._id]) || reOrderOption?.[0] || null;
  const activeSelectId = activeDropdown?._id || null;
  const activeDevId = hasReOrders ? reOrderOption[0]?._id : null;
  /* `clusterId` is the order this row currently holds, so a load runs only until the two agree. */
  const loadedClusterId = item?.clusterId || item?._id;

  useEffect(() => {
    if (activeSelectId && activeSelectId !== loadedClusterId) {
      getReorderData?.(activeSelectId, index);
    }
  }, [activeSelectId, loadedClusterId, index]);

  const handleLinkPage = () => {
    const obj = { archived: state?.activeList !== 'open' || false, devOrderId: item._id, deleted: state?.activeList === 'bin' || false };
    dispatch(getReorderList(obj));
    setState((prevData) => {
      return {
        ...prevData,
        isLinkOpen: true,
        isSelectAll: false,
        LinkId: item?._id,
      };
    });
  };

  return (
    <>
      <BombiixCard className="product-card mt-4 mb-0">
        <BombiixCard.Header>
          <BombiixCard.Actions style={{ width: '100%' }}>
            {!isSupplierLoggedIn && state?.activeList !== 'bin' && state?.activeList !== 'reorder' && (ProductAddEditAccess || ProductDeleteAccess) && (
              <li>
                <NewCheckbox
                  aria-label="Select"
                  checked={checkStatus}
                  onChange={(e) => {
                    handleCheckbox(e, item?.clusterId ? item?.clusterId : item?._id);
                  }}
                />
              </li>
            )}
            {ProductAddEditAccess && !isSupplierLoggedIn && isEditIconVisible && (
              <li>
                <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                  <button
                    type="button"
                    className="bombiix-icon-container bombiix-icon-container--blue"
                    onClick={() => handleEdit(activeSelectId || item?._id)}
                    aria-label="Edit"
                  >
                    <EditIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {ProductAddEditAccess && !isSupplierLoggedIn && (state?.activeList === 'open' || state?.activeList === 'reorder') && (
              <li>
                <CustomTooltip tooltipText={t('FILTER.DUPLICATE_DATA_PACK')}>
                  <button
                    type="button"
                    className="bombiix-icon-container bombiix-icon-container--blue"
                    onClick={() => handleDuplicateProduct(activeSelectId || item?._id)}
                    aria-label="Duplicate Data Pack"
                  >
                    <DuplicateIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {!isSupplierLoggedIn && (
              <li>
                <CustomTooltip tooltipText={t('FILTER.COMPLETION_INDECATORS')}>
                  <button
                    type="button"
                    className="bombiix-icon-container bombiix-icon-container--blue"
                    onClick={() => handleCompletionIndicator(item)}
                    aria-label="Completion Indicator"
                  >
                    <ProgressIndicatorIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {ProductAddEditAccess && ProductGroupAddEditAccess && ProductGroupToggle && !isSupplierLoggedIn && state?.activeList === 'open' && (
              <li>
                <CustomTooltip tooltipText={t('FILTER.ADD_TO_GRUP')}>
                  <button
                    type="button"
                    className="bombiix-icon-container bombiix-icon-container--blue"
                    onClick={() => handleProductGroup(activeSelectId ? { _id: activeSelectId } : item)}
                    aria-label="Add to Group"
                  >
                    <ProductGroupIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {((userRole !== 'Admin' && (['open', 'reorder'].includes(state?.activeList) ? archieveTechPackState : deleteTechPackState)) ||
              userRole === 'Admin') &&
              state?.activeList !== 'bin' &&
              ProductAddEditAccess && (
                <li>
                  <CustomTooltip tooltipText={state?.activeList === 'open' ? t('FILTER.ARCHIVE') : t('FILTER.UNARCHIVE')}>
                    <button
                      type="button"
                      className="bombiix-icon-container bombiix-icon-container--dark"
                      onClick={() => handleArchiveProduct(activeSelectId || item?._id)}
                      aria-label={state?.activeList === 'open' ? 'Archive' : 'Unarchive'}
                    >
                      <ArchiveIcon size={24} />
                    </button>
                  </CustomTooltip>
                </li>
              )}
            {state?.activeList === 'archived' && (
              <>
                {ProductAddEditAccess && (
                  <li>
                    <CustomTooltip tooltipText={item?.archieveComment ? item.archieveComment : t('FILTER.PRODUCT_BEING_ARCHIVED')}>
                      <button
                        type="button"
                        className="bombiix-icon-container"
                        aria-label={item?.archieveComment ? item.archieveComment : 'No comment was given at the time of the Product being archived.'}
                      >
                        <ProductInfoIcon size={24} />
                      </button>
                    </CustomTooltip>
                  </li>
                )}
                {((deleteTechPackState && userRole !== 'Admin') || userRole === 'Admin') && ProductDeleteAccess && (
                  <li>
                    <CustomTooltip tooltipText={t('FILTER.TRASH')}>
                      <button
                        type="button"
                        className="bombiix-icon-container bombiix-icon-container--red"
                        onClick={() => handleTrashProduct(item?._id)}
                        aria-label="Trash"
                      >
                        <DeleteIcon size={24} />
                      </button>
                    </CustomTooltip>
                  </li>
                )}
              </>
            )}
            {((trashTechPackState && userRole !== 'Admin') || userRole === 'Admin') && state?.activeList === 'bin' && ProductDeleteAccess && (
              <li>
                <CustomTooltip tooltipText={t('TAKING_DATES.DELETES')}>
                  <button
                    type="button"
                    className="bombiix-icon-container bombiix-icon-container--red"
                    onClick={() => handleDelete(item?._id)}
                    aria-label="Delete"
                  >
                    <DeleteIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {!isSupplierLoggedIn && state?.activeList === 'open' && item?.parentDevOrderId && ProductAddEditAccess && (
              <li>
                <CustomTooltip tooltipText={t('FILTER.UNLIKE')}>
                  <button type="button" className="bombiix-icon-container" onClick={() => handleUnlink()} aria-label="unlink">
                    <UnlinkIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {!isSupplierLoggedIn && (
              <li style={{ marginLeft: 'auto' }}>
                <CustomTooltip tooltipText={t('TOP_NAVBAR.HISTORY')}>
                  <Link to={`/development-order/${item?._id}/history`} className="bombiix-icon-container" aria-label="History">
                    <HistoryIcon size={24} />
                  </Link>
                </CustomTooltip>
              </li>
            )}
          </BombiixCard.Actions>

          <BombiixCard.Thumb
            enlargeOnHover={hasRealImage(activeDropdown)}
            alt={item?.number}
            src={imgSrc(imgElement(activeDropdown))}
            previewSrc={previewImgSrc(imgElement(activeDropdown))}
          />
        </BombiixCard.Header>
        <BombiixCard.Body onClick={() => expandCollapse(index)}>
          <ReorderTitleComponent
            key={index}
            item={item}
            activeDropdown={activeDropdown}
            activeSelectId={activeSelectId}
            onBtnClick={onBtnClick}
            activeDevId={activeDevId}
            reOrderOption={reOrderOption}
            reOrderList={reOrderList}
            handleLinkPage={handleLinkPage}
          />
          <BombiixCard.Meta>
            <li>{shortenandHoverString(item?.description, 30)}</li>
            <li>{item?.brand}</li>
            {subBrandAccess && <li>{item?.subBrand || '-'}</li>}
            <li>
              {!item?.season && !item?.month && !item?.year && '-'}
              {item?.season && `${item?.season}`}
              {item?.month && `, ${item?.month}`}
              {item?.year && `, ${item.year}`}
            </li>
            <li>
              {`${item?.catOne}, ${item?.catTwo}`}
              {item?.catThree && `, ${item?.catThree}`}
              {item?.catFour && `, ${item?.catFour}`}
            </li>
            <li>
              <strong className="d-block">
                {t('TAKING_DATES.STATES')}: {item?.status ? item?.status?.name : '-'}
              </strong>
            </li>
            <div className={expandCard === index ? 'd-block' : 'd-none'}>
              {!isEmpty(item?.dynamicColumn) &&
                item?.dynamicColumn.map((eleDynamic) => {
                  return eleDynamic?.isActive ? (
                    <li>
                      <strong>{eleDynamic?.keyName}: </strong>
                      {showDynamicValue(eleDynamic)}
                    </li>
                  ) : null;
                })}
              <li>
                <strong>{t('FILTER.COLOURWAYS')} </strong>
                {!isEmpty(item?.colourLibraries) && item?.colourLibraries.join(', ')}
              </li>
              <li>
                <strong>{t('FILTER.GRADING_LIBRARY')} </strong>
                {!isEmpty(item?.gradingGroups) && item?.gradingGroups.map((ele) => ele.label).join(', ')}
              </li>
              <li>
                <strong>{t('FILTER.GRADING_GROUP_SIZES')} </strong>
                {!isEmpty(item?.sizeRanges) && item?.sizeRanges.join(', ')}
              </li>
              <li>
                <strong>{t('FILTER.SIZES')} </strong>
                {!isEmpty(item?.productSizes) && item?.productSizes.join(', ')}
              </li>
              <li>
                <strong>{t('FILTER.SIZE_RANGES')} </strong>
                {!isEmpty(item?.productSizeRanges) && item?.productSizeRanges.join(', ')}
              </li>
              {lengthEnabled && (
                <li>
                  <strong>{lengthLabel} </strong>
                  {!isEmpty(item?.legLengths) && item?.legLengths.join(', ')}
                </li>
              )}
              <li>
                <strong>{t('FILTER.PRODUCT_TYPE')} </strong>
                {item?.supplierType}
              </li>
              <li>
                <strong>{t('FILTER.SUPPLIER')} </strong>
                {isSupplierLoggedIn && loggedInUserInfo?.role === 'Supplier'
                  ? supplierCompany?.supplierName
                  : !isEmpty(item?.suppliers) && item?.suppliers.join(', ')}
              </li>
              <li className="d-flex">
                <strong>{t('FILTER.CREATING_DATE_TIME')}&nbsp;</strong>
                {item?.createdBy} {moment(item.createdAt).format('DD-MMMM-YYYY H:mm')}
              </li>
              {item?.lastModifiedAt && !moment(item?.createdAt).isSame(moment(item?.lastModifiedAt)) && (
                <li className="d-flex">
                  <strong>{t('FILTER.MODIFID_DATE_TIME')}&nbsp;</strong>
                  {item?.modifiedBy} {moment(item.lastModifiedAt).format('DD-MMMM-YYYY H:mm')}
                </li>
              )}
            </div>
          </BombiixCard.Meta>
        </BombiixCard.Body>
      </BombiixCard>
      {cardState?.isUnlinkOpen && (
        <ConfirmationPopup
          onOpen={cardState?.isUnlinkOpen}
          onConfirm={() => handleUnlinkConfirmed(activeSelectId)}
          title={t('DEVLOPMENT_ORDER.PRODUCT_LIST_CONTAINER.PRODUCT_CARD_LIST.UNLIKE_PRODUCT')}
          message={t('DEVLOPMENT_ORDER.PRODUCT_LIST_CONTAINER.PRODUCT_CARD_LIST.PRODUCT_UNLIKE_MESSAGE')}
          toggle={closeUnlinkModal}
          isCommentOn={false}
        />
      )}
    </>
  );
}

export default ProductCardList;
